import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import connectorsettingsService from '../services/connectorsettingsService';
import connectorService from '../services/connectorService';
import './ConnectorSettingForm.css';

function ConnectorSettingForm({ mode = 'create' }) {
  const { id, connectorid } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    CONNECTORID: connectorid || '',
    ACCOUNTID: '',
    SETTINGNAME: '',
    SETTINGVALUE: ''
  });

  useEffect(() => {
    const initializeForm = async () => {
      if (connectorid) {
        setFormData(prev => ({ ...prev, CONNECTORID: connectorid }));
        // Fetch connector to get accountid
        await fetchConnectorAccountId();
      }

      if (mode !== 'create' && id) {
        await fetchSetting();
      }
    };

    initializeForm();
  }, [id, mode, connectorid]);

  const fetchConnectorAccountId = async () => {
    try {
      const response = await connectorService.getConnectorById(connectorid);
      if (response && response.accountid) {
        setFormData(prev => ({ ...prev, ACCOUNTID: response.accountid }));
      }
    } catch (err) {
      console.error('Error fetching connector accountid:', err);
    }
  };

  const fetchSetting = async () => {
    try {
      setLoading(true);
      const response = await connectorsettingsService.getConnectorSettingById(id);
      if (response.setting) {
        setFormData(response.setting);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch setting');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.SETTINGNAME) {
      setError('Setting Name is required');
      return;
    }

    try {
      setLoading(true);
      if (mode === 'create') {
        await connectorsettingsService.createConnectorSetting(formData);
      } else if (mode === 'edit') {
        await connectorsettingsService.updateConnectorSetting(id, formData);
      }
      navigate(`/connectors/${connectorid}/edit#settings`);
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${mode} setting`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this setting?')) {
      return;
    }

    try {
      setLoading(true);
      await connectorsettingsService.deleteConnectorSetting(id);
      navigate(`/connectors/${connectorid}/edit#settings`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete setting');
      setLoading(false);
    }
  };

  if (loading && mode !== 'create') {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="form-container">
      <div className="form-header">
        <h2>
          {mode === 'create' && 'Add Setting'}
          {mode === 'edit' && 'Edit Setting'}
          {mode === 'view' && 'Setting Details'}
        </h2>
        <button
          type="button"
          onClick={() => navigate(`/connectors/${connectorid}/edit#settings`)}
          className="btn-secondary"
        >
          Back to Connector
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-section">
          <h3>Setting Information</h3>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="SETTINGNAME">Setting Name *</label>
              <select
                id="SETTINGNAME"
                name="SETTINGNAME"
                value={formData.SETTINGNAME || ''}
                onChange={handleChange}
                disabled={mode === 'view'}
                required
              >
                <option value="">Select a setting name...</option>
                <option value="ACCESSKEYID">ACCESSKEYID</option>
                <option value="ACCESSTOKEN">ACCESSTOKEN</option>
                <option value="ACCESSTOKENTIME">ACCESSTOKENTIME</option>
                <option value="AGENTID">AGENTID</option>
                <option value="AGENTPASSWORD">AGENTPASSWORD</option>
                <option value="APIKEY">APIKEY</option>
                <option value="APIPASSWORD">APIPASSWORD</option>
                <option value="APIURL">APIURL</option>
                <option value="APIUSER">APIUSER</option>
                <option value="APPKEY">APPKEY</option>
                <option value="AUTH0URL">AUTH0URL</option>
                <option value="AUTHHOST">AUTHHOST</option>
                <option value="AUTHMETHOD">AUTHMETHOD</option>
                <option value="AZUREACCOUNT">AZUREACCOUNT</option>
                <option value="BASEURL">BASEURL</option>
                <option value="BLOCKCHANNEL">BLOCKCHANNEL</option>
                <option value="BLOCKMARKETCODE">BLOCKMARKETCODE</option>
                <option value="BLOCKMASK">BLOCKMASK</option>
                <option value="BLOCKRATECODE">BLOCKRATECODE</option>
                <option value="BLOCKRESTYPE">BLOCKRESTYPE</option>
                <option value="BLOCKSOURCE">BLOCKSOURCE</option>
                <option value="BPNID">BPNID</option>
                <option value="BUCKETNAME">BUCKETNAME</option>
                <option value="CHAIN">CHAIN</option>
                <option value="CHANNELCLIENTID">CHANNELCLIENTID</option>
                <option value="CHANNELPASSWORD">CHANNELPASSWORD</option>
                <option value="CHANNELUSER">CHANNELUSER</option>
                <option value="CHILDBUCKETS">CHILDBUCKETS</option>
                <option value="CLIENTID">CLIENTID</option>
                <option value="CLIENTPASSWORD">CLIENTPASSWORD</option>
                <option value="CLIENTSECRET">CLIENTSECRET</option>
                <option value="COMPANY REVENUE FILTER">COMPANY REVENUE FILTER</option>
                <option value="COMPANYREVENUEFILTER">COMPANYREVENUEFILTER</option>
                <option value="CONTAINER">CONTAINER</option>
                <option value="CRYPTOKEY">CRYPTOKEY</option>
                <option value="CRYPTOVECTOR">CRYPTOVECTOR</option>
                <option value="DATABASENAME">DATABASENAME</option>
                <option value="DBNAME">DBNAME</option>
                <option value="DBPASSWORD">DBPASSWORD</option>
                <option value="DBSID">DBSID</option>
                <option value="DBUSER">DBUSER</option>
                <option value="DEFAULTCHAINCODE">DEFAULTCHAINCODE</option>
                <option value="DEFAULTRESORT">DEFAULTRESORT</option>
                <option value="DEQUEUECOUNT">DEQUEUECOUNT</option>
                <option value="DESTINATIONENTITY">DESTINATIONENTITY</option>
                <option value="DESTINATIONSYSTEM">DESTINATIONSYSTEM</option>
                <option value="DOMAIN">DOMAIN</option>
                <option value="ELIGIBLEORIGIN">ELIGIBLEORIGIN</option>
                <option value="ELIGIBLERATECODE">ELIGIBLERATECODE</option>
                <option value="ENTERPRISEID">ENTERPRISEID</option>
                <option value="ENVIRONMENT_NAME">ENVIRONMENT_NAME</option>
                <option value="EVENTRENTALCODE">EVENTRENTALCODE</option>
                <option value="EVENTSETUPCODE">EVENTSETUPCODE</option>
                <option value="EVENTSTATUS">EVENTSTATUS</option>
                <option value="EVENTTYPE">EVENTTYPE</option>
                <option value="EXCLUDEDPACKAGESS">EXCLUDEDPACKAGESS</option>
                <option value="EXTERNALSYSTEM">EXTERNALSYSTEM</option>
                <option value="GATEWAYURL">GATEWAYURL</option>
                <option value="GUARANTEETYPE">GUARANTEETYPE</option>
                <option value="HOST">HOST</option>
                <option value="HOSTNAME">HOSTNAME</option>
                <option value="HOSTURL">HOSTURL</option>
                <option value="HOTELCODE">HOTELCODE</option>
                <option value="HOTELID">HOTELID</option>
                <option value="IFCEMAIL">IFCEMAIL</option>
                <option value="INBOUNDAPIKEY">INBOUNDAPIKEY</option>
                <option value="INBOUNDPASSWORD">INBOUNDPASSWORD</option>
                <option value="INBOUNDUSERNAME">INBOUNDUSERNAME</option>
                <option value="INSERTUSER">INSERTUSER</option>
                <option value="LAMBDACONCURRENCY">LAMBDACONCURRENCY</option>
                <option value="LAMBDAMEMORY">LAMBDAMEMORY</option>
                <option value="LAMBDATIMEOUT">LAMBDATIMEOUT</option>
                <option value="MERCHANT">MERCHANT</option>
                <option value="MESSAGEFORMAT">MESSAGEFORMAT</option>
                <option value="NAMESPACE">NAMESPACE</option>
                <option value="NOTIFYEMAIL">NOTIFYEMAIL</option>
                <option value="OPERAORS">OPERAORS</option>
                <option value="ORIGINENTITY">ORIGINENTITY</option>
                <option value="ORIGINSYSTEM">ORIGINSYSTEM</option>
                <option value="OUTBOUNDPASSWORD">OUTBOUNDPASSWORD</option>
                <option value="OUTBOUNDUSERNAME">OUTBOUNDUSERNAME</option>
                <option value="PASSWORD">PASSWORD</option>
                <option value="PATH">PATH</option>
                <option value="PAYMENTTYPE">PAYMENTTYPE</option>
                <option value="PORT">PORT</option>
                <option value="PROFILESTAYCOUNT">PROFILESTAYCOUNT</option>
                <option value="PROPERTYID">PROPERTYID</option>
                <option value="PULLURL">PULLURL</option>
                <option value="PUSHURL">PUSHURL</option>
                <option value="RATECODE">RATECODE</option>
                <option value="REFRESHTOKEN">REFRESHTOKEN</option>
                <option value="REGION">REGION</option>
                <option value="RESHOST">RESHOST</option>
                <option value="RESORT">RESORT</option>
                <option value="RESRATECODE">RESRATECODE</option>
                <option value="ROOMTYPE">ROOMTYPE</option>
                <option value="ROOMTYPES">ROOMTYPES</option>
                <option value="S3ARN">S3ARN</option>
                <option value="S3FOLDER">S3FOLDER</option>
                <option value="SASTOKEN">SASTOKEN</option>
                <option value="SECRETACCESSKEY">SECRETACCESSKEY</option>
                <option value="SECRETKEY">SECRETKEY</option>
                <option value="SLEEPTIME">SLEEPTIME</option>
                <option value="SQSBATCHSIZE">SQSBATCHSIZE</option>
                <option value="SUBFOLDER">SUBFOLDER</option>
                <option value="SYSTEMID">SYSTEMID</option>
                <option value="SYSTEMTYPE">SYSTEMTYPE</option>
                <option value="TERMINAL">TERMINAL</option>
                <option value="THREADS">THREADS</option>
                <option value="TOKEN">TOKEN</option>
                <option value="TOKENTIME">TOKENTIME</option>
                <option value="TRACEDEPT">TRACEDEPT</option>
                <option value="USER">USER</option>
                <option value="USERNAME">USERNAME</option>
                <option value="VIP">VIP</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="ACCOUNTID">Account ID</label>
              <input
                type="number"
                id="ACCOUNTID"
                name="ACCOUNTID"
                value={formData.ACCOUNTID || ''}
                onChange={handleChange}
                disabled={true}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width">
              <label htmlFor="SETTINGVALUE">Setting Value</label>
              <textarea
                id="SETTINGVALUE"
                name="SETTINGVALUE"
                value={formData.SETTINGVALUE || ''}
                onChange={handleChange}
                disabled={mode === 'view'}
                rows="6"
                placeholder="Enter the setting value (supports multi-line text)"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="CONNECTORID">Connector ID</label>
              <input
                type="number"
                id="CONNECTORID"
                name="CONNECTORID"
                value={formData.CONNECTORID || ''}
                onChange={handleChange}
                disabled={true}
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          {mode === 'view' && (
            <>
              <button
                type="button"
                onClick={() => navigate(`/connector/${connectorid}/setting/${id}/edit`)}
                className="btn-primary"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="btn-danger"
                disabled={loading}
              >
                Delete
              </button>
            </>
          )}
          {(mode === 'create' || mode === 'edit') && (
            <>
              <button
                type="submit"
                className="btn-primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : (mode === 'create' ? 'Create' : 'Update')}
              </button>
              <button
                type="button"
                onClick={() => navigate(`/connectors/${connectorid}/edit#settings`)}
                className="btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
}

export default ConnectorSettingForm;
